import Phaser from 'phaser'
import {
  Grid,
  GridSize,
  SpinTiming,
  type SymbolKey,
} from '@/config/SlotConfig'
import { Reel } from './Reel'

/**
 * Reel 网格（COLS × ROWS, 由 Grid 配置驱动 — Candy 是 6×5）
 *
 * 协调 N 列 Reel 的启停（staggered）：
 * - spin(): COLS 列依次启动 (间隔 STAGGER_MS)
 * - stop(grid): 给定 COLS×ROWS grid，依次停止
 *
 * Geometry mask 限定可见区域；cluster_tumble 的 cascade 阶段由 GameScene
 * 直接操作可见 SlotSymbol 实例，不经过 Reel 的滚动。
 */
export class ReelGrid {
  private readonly scene: Phaser.Scene
  private reels: Reel[] = []
  private spinning = false

  constructor(scene: Phaser.Scene) {
    this.scene = scene
    this.createReels()
    this.applyGridMask()
  }

  private createReels(): void {
    for (let col = 0; col < Grid.COLS; col++) {
      this.reels.push(new Reel(this.scene, col))
    }
  }

  /**
   * 给本 ReelGrid 创建的 SlotSymbol 实例上一个矩形 mask, 限定只在网格内显示
   *
   * 不再用 scene.children.list 粗筛 (旧实现会误伤背景图 / Jackpot header 等
   * 落在 grid 区域的 Image)。改成显式遍历 reel 持有的 symbols。
   */
  private applyGridMask(): void {
    const maskShape = this.scene.make.graphics()
    maskShape.fillStyle(0xffffff)
    maskShape.fillRect(
      Grid.ORIGIN_X,
      Grid.ORIGIN_Y,
      GridSize.WIDTH,
      GridSize.HEIGHT,
    )
    const mask = maskShape.createGeometryMask()

    for (const reel of this.reels) {
      for (const sym of reel.getAllSymbols()) {
        sym.setMask(mask)
      }
    }
  }

  /** 启动整组 reel(staggered) */
  spin(): void {
    if (this.spinning) return
    this.spinning = true

    this.reels.forEach((reel, i) => {
      this.scene.time.delayedCall(i * SpinTiming.STAGGER_MS, () => {
        reel.startSpin()
      })
    })
  }

  /**
   * 给定 5x3 grid 结果, 让 5 个 reel 依次减速停止
   * grid[col][row]: col 列 row 行的 symbol
   * @returns Promise resolved when all reels stopped
   */
  stop(grid: SymbolKey[][]): Promise<void> {
    return new Promise((resolve) => {
      let stoppedCount = 0
      this.reels.forEach((reel, col) => {
        const colSymbols = grid[col] ?? []
        // 错开停止
        this.scene.time.delayedCall(col * SpinTiming.STAGGER_MS, () => {
          reel.requestStop(colSymbols, () => {
            stoppedCount++
            if (stoppedCount === this.reels.length) {
              this.spinning = false
              resolve()
            }
          })
        })
      })
    })
  }

  isSpinning(): boolean {
    return this.spinning
  }

  /** 取指定 (col, row) 的 SlotSymbol 实例（中奖动画用） */
  getSymbolObjectAt(col: number, row: number) {
    return this.reels[col]?.getVisibleSymbolObjects()[row]
  }

  /** 取指定列的 ROWS 个可见 SlotSymbol（顶到底） */
  getVisibleColumn(col: number) {
    return this.reels[col]?.getVisibleSymbolObjects() ?? []
  }

  /** 由 Scene update 转发 */
  update(time: number, deltaMs: number): void {
    this.reels.forEach((reel) => reel.update(time, deltaMs))
  }
}
